'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Maximize2, ChevronLeft, ChevronRight } from 'lucide-react';

interface CanvasPreviewProps {
  images: string[];
  productName: string;
  imageAltTexts?: string[];
  productId?: string;
  uploadedFileUrl?: string;
}

export function CanvasPreview({ images, productName, imageAltTexts, productId, uploadedFileUrl }: CanvasPreviewProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [variantImage, setVariantImage] = useState<string | null>(null);
  const [temporaryImages, setTemporaryImages] = useState<string[]>([]);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleVariantPreview = (event: Event) => {
      const detail = (event as CustomEvent<{
        productId?: string;
        imageUrl?: string | null;
        imageUrls?: string[];
        replaceImages?: boolean;
      }>).detail;
      if (productId && detail?.productId && detail.productId !== productId) return;
      const imageUrls = Array.isArray(detail?.imageUrls)
        ? Array.from(new Set(detail.imageUrls.filter((url): url is string => typeof url === 'string' && url.length > 0)))
        : [];
      if (detail?.replaceImages && imageUrls.length > 0) {
        setTemporaryImages(imageUrls);
        setVariantImage(null);
        setCurrentImageIndex(0);
        return;
      }
      setTemporaryImages([]);
      setVariantImage(detail?.imageUrl || null);
      setCurrentImageIndex(0);
    };

    window.addEventListener('printen:product-preview-image', handleVariantPreview);
    return () => window.removeEventListener('printen:product-preview-image', handleVariantPreview);
  }, [productId]);

  const displayImages = temporaryImages.length > 0
    ? temporaryImages
    : [
        ...(uploadedFileUrl ? [uploadedFileUrl] : []),
        ...(variantImage ? [variantImage] : []),
        ...images.filter((image) => image !== variantImage),
      ];
  const currentImage = displayImages[currentImageIndex] || images[0];
  const usesOriginalGallery = temporaryImages.length === 0 && !uploadedFileUrl && !variantImage;
  const imageAlt = (index: number) => usesOriginalGallery
    ? imageAltTexts?.[index] || `${productName} - productafbeelding ${index + 1}`
    : productName;
  const currentImageAlt = imageAlt(currentImageIndex);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  return (
    <div className="min-w-0 max-w-full space-y-3 overflow-hidden sm:space-y-4">
      {/* Main Image */}
      <div className="group relative h-[min(82vw,360px)] max-w-full overflow-hidden rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 sm:h-auto sm:aspect-square">
        <Image
          src={currentImage}
          alt={currentImageAlt}
          fill
          // Zonder sizes downloadt fill een viewport-brede variant; de kolom is
          // maar ~40% van het scherm, dus dit halveert de LCP-bytes ruimschoots.
          sizes="(max-width: 1023px) 100vw, 40vw"
          className="object-cover"
          preload
        />

        {/* Fullscreen Button */}
        <button
          onClick={() => setIsFullscreen(true)}
          className="absolute left-4 top-4 rounded-lg bg-white/85 p-3 shadow-lg transition-opacity hover:bg-white sm:opacity-0 sm:group-hover:opacity-100"
          aria-label="Afbeelding vergroten"
        >
          <Maximize2 className="h-6 w-6" />
        </button>

        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 shadow-lg transition hover:bg-white"
              aria-label="Vorige afbeelding"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 shadow-lg transition hover:bg-white"
              aria-label="Volgende afbeelding"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white text-sm rounded-full">
            {currentImageIndex + 1} / {displayImages.length}
          </div>
        )}

        {/* Uploaded File Indicator */}
        {uploadedFileUrl && currentImageIndex === 0 && (
          <div className="absolute left-4 top-20 rounded-lg bg-green-500 px-3 py-1 text-sm font-medium text-white shadow-lg">
            Je ontwerp
          </div>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {displayImages.length > 1 && (
        <div className="relative min-w-0 max-w-full overflow-hidden">
          <div
            ref={thumbnailsRef}
            className="flex gap-2 overflow-x-auto scroll-smooth pb-2 [-ms-overflow-style:none] [scrollbar-width:thin] [scrollbar-color:#cbd5e1_transparent] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-transparent"
            aria-label="Productafbeeldingen"
            onWheel={(e) => {
              const el = e.currentTarget;
              const atStart = el.scrollLeft <= 0 && e.deltaY < 0;
              const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1 && e.deltaY > 0;
              if (!atStart && !atEnd) {
                el.scrollLeft += e.deltaY;
                e.preventDefault();
              }
            }}
          >
            {displayImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`relative h-20 w-20 flex-none overflow-hidden rounded-lg sm:h-24 sm:w-24 ${
                  index === currentImageIndex
                    ? 'ring-2 ring-blue-600'
                    : 'ring-1 ring-gray-200 hover:ring-gray-300'
                }`}
              >
                <Image
                  src={image}
                  alt={imageAlt(index)}
                  fill
                  sizes="96px"
                  className="object-cover"
                />
                {uploadedFileUrl && index === 0 && (
                  <div className="absolute inset-0 bg-gradient-to-t from-green-500/20 to-transparent" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen Dialog */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-screen-lg h-screen p-0">
          <DialogTitle className="sr-only">{productName} - Voorbeeld</DialogTitle>
          <div className="relative w-full h-full bg-black">
            <Image
              src={currentImage}
              alt={currentImageAlt}
              fill
              className="object-contain"
            />

            {/* Close Button */}
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
            >
              ✕
            </button>

            {/* Navigation */}
            {displayImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
